import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import LoadingSpinner from './LoadingSpinner';

// Define a consistent color palette
const colors = {
  primary: '#6366F1', // Indigo
  primaryLight: '#A5B4FC',
  primaryDark: '#4338CA',
  secondary: '#EC4899', // Pink
  success: '#10B981', // Emerald
  warning: '#F59E0B', // Amber
  error: '#EF4444', // Red
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  }
};

// Main page container
export const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  minHeight: '100vh',
  backgroundColor: colors.gray[50],
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

// Page header section
export const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

// Page title with gradient
export const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '2.5rem',
  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(1),
  textAlign: 'left',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

// Page subtitle - on mobile only, extra line-height/padding so descenders (g, y) aren't clipped
export const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  color: colors.gray[600],
  textAlign: 'left',
  lineHeight: 1.6,
  [theme.breakpoints.only('xs')]: {
    lineHeight: 1.65,
    overflow: 'visible',
    paddingBottom: '0.25em',
  },
}));

// Main content card
export const PageCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  borderRadius: theme.spacing(2),
  border: `1px solid ${colors.gray[200]}`,
  height: '100%',
}));

// Card content
export const PageCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

// Loading container
export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

interface PageSectionProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  loading?: boolean;
  loadingText?: string;
  /** When true, title/subtitle are hidden on xs only (phones); sm and up unchanged */
  hideTitleOnMobile?: boolean;
}

const PageSection: React.FC<PageSectionProps> = ({
  title,
  subtitle,
  children,
  loading = false,
  loadingText = 'Loading...',
  hideTitleOnMobile = false,
}) => {
  if (loading) {
    return (
      <PageContainer>
        <PageCard>
          <PageCardContent>
            <LoadingSpinner message={loadingText} />
          </PageCardContent>
        </PageCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {(title || subtitle) && (
        <PageHeader sx={hideTitleOnMobile ? { display: { xs: 'none', sm: 'block' } } : undefined}>
          {title && (
            <PageTitle>
              {title}
            </PageTitle>
          )}
          {subtitle && (
            <PageSubtitle>
              {subtitle}
            </PageSubtitle>
          )}
        </PageHeader>
      )}

      <PageCard>
        <PageCardContent>
          {children}
        </PageCardContent>
      </PageCard>
    </PageContainer>
  );
};

export default PageSection;
